import React from 'react'
import Link from 'next/link'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { Link as ScrollLink, Events, scrollSpy, scroller } from 'react-scroll'

import { Text } from '@wartek-id/text'

import { GATrackEvent } from 'utils/googleAnalytics'
import { useScreenSizeContext } from 'utils/hooks/context/useScreenSizeContext'
import {
  getWebinarTabLabel,
  useWebinarTabCount,
} from 'app/PelatihanMandiriPage/hooks/useWebinarTabCount'

import type { TabbedNavItem } from 'types/microlearning'

const SHOW_PROGRES_TOPIK = false

const trackClick = (actionType: string) => {
  GATrackEvent({
    eventName: 'learning_activity',
    eventParams: {
      event_trigger: 'ab_test_tabbed_menu',
      action_type: actionType,
    },
    sendDefaultParams: true,
  })
}

const getDefaultNavItems = (webinarCount?: number): TabbedNavItem[] => [
  {
    name: 'topik',
    label: 'Topik',
    href: `/${
      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH || 'pelatihan-mandiri'
    }/daftar-topik`,
  },
  {
    name: 'aksi-nyata',
    label: 'Aksi Nyata',
    href: `/${
      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH || 'pelatihan-mandiri'
    }/daftar-aksi-nyata`,
  },
  {
    name: 'webinar',
    label: getWebinarTabLabel(webinarCount),
    href: `/${
      process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH || 'pelatihan-mandiri'
    }/webinar`,
  },
]

const scrollNavs: TabbedNavItem[] = [
  {
    name: 'rekomendasi-belajar',
    label: 'Rekomendasi belajar',
    image:
      'https://storage.googleapis.com/microlearning-public-production/web-assets/kumpulan-tema-icon.png',
  },
  ...(SHOW_PROGRES_TOPIK
    ? [
        {
          name: 'progress-topik',
          label: 'Progres topik',
          image:
            'https://storage.googleapis.com/microlearning-public-production/web-assets/progress-topic-icon.png',
        },
      ]
    : []),
  {
    name: 'webinar',
    label: 'Webinar',
    image:
      'https://storage.googleapis.com/microlearning-public-production/web-assets/webinar-icon.png',
  },
]

const TabbedNav = ({
  activeMenu,
  navItems,
  absolutePosition = false,
  scrollClicked,
  isHideScrollNav = false,
  className,
}: {
  activeMenu?: string
  navItems?: TabbedNavItem[]
  absolutePosition?: boolean
  className?: string
  scrollClicked?: ({ name, label }) => void
  isHideScrollNav?: boolean
}) => {
  const { isDesktop } = useScreenSizeContext()
  const { pathname } = useRouter()
  const webinarCount = useWebinarTabCount(!navItems)
  const items = navItems ?? getDefaultNavItems(webinarCount)
  const isHomepagePelatihanMandiri = pathname === '/pelatihan-mandiri'
  const [isSpyActive, setIsSpyActive] = React.useState(true)

  const handleScroll = ({ name, label }) => {
    scroller.scrollTo(name, {
      duration: 500,
      delay: 0,
      smooth: 'smooth',
      offset: -230,
    })
    scrollClicked({ name, label })
  }

  useEffect(() => {
    Events.scrollEvent.register('begin', () => {
      setIsSpyActive(false)
    })

    Events.scrollEvent.register('end', () => {
      setIsSpyActive(true)
    })

    scrollSpy.update()

    return () => {
      Events.scrollEvent.remove('begin')
      Events.scrollEvent.remove('end')
    }
  })

  return (
    <div
      className={clsx(
        'left-1/2 -translate-x-1/2 w-full z-20 bg-white -mt-0.5',
        absolutePosition ? 'absolute' : 'fixed',
        !isDesktop && 'max-w-xl',
        className
      )}
    >
      <div className="flex items-stretch -mx-2 h-9 border-b">
        {items.map(({ name, label, href }) => (
          <div key={name} className="px-2 flex-1 h-full text-center">
            <Link href={href}>
              {/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */}
              <Text
                data-testid={name}
                onClick={() => trackClick(name)}
                className={clsx(
                  'inline-block pb-2 border-b-4',
                  activeMenu === name
                    ? 'border-icon-informational'
                    : 'border-transparent'
                )}
                variant="action"
                color={activeMenu === name ? 'primary' : 'subdued'}
                as="a"
                href={href}
              >
                {label}
              </Text>
            </Link>
          </div>
        ))}
      </div>
      {isHomepagePelatihanMandiri && !isHideScrollNav && (
        <div className="grid grid-cols-2 gap-2 items-stretch p-4 shadow-md">
          {scrollNavs.map(({ name, label, image }) => (
            <div key={name} onClick={() => handleScroll({ name, label })}>
              <ScrollLink
                to={name}
                offset={-460}
                spy={isSpyActive}
                activeClass="border-blue-50"
                className={`pointer-events-none py-2 block border rounded-md text-center h-full`}
              >
                <div>
                  <img
                    src={image}
                    width={32}
                    height={32}
                    alt={name}
                    className="mb-1 mx-auto"
                  />
                  <div className="text-xs font-bold">{label}</div>
                </div>
              </ScrollLink>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default TabbedNav
